// @ts-nocheck
/**
 * Touch Optimized Components - Enhanced touch interactions for mobile devices
 * Features: Touch feedback, swipe gestures, hold actions, accessibility
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { cn } from '@/lib/utils';

// Touch feedback types
type FeedbackType = 'light' | 'medium' | 'heavy' | 'selection' | 'impact';

// Gesture types
interface SwipeGesture {
  direction: 'left' | 'right' | 'up' | 'down';
  velocity: number;
  distance: number;
}

interface TouchOptimizedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  hapticFeedback?: FeedbackType;
  onLongPress?: () => void;
  longPressDelay?: number;
}

interface SwipeableCardProps {
  children: React.ReactNode;
  className?: string;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  swipeThreshold?: number;
  leftAction?: {
    icon: React.ReactNode;
    label: string;
    color?: string;
    background?: string;
  };
  rightAction?: {
    icon: React.ReactNode;
    label: string;
    color?: string;
    background?: string;
  };
}

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  pullThreshold?: number;
  maxPullDistance?: number;
  refreshing?: boolean;
}

// Haptic feedback utility
const triggerHapticFeedback = (type: FeedbackType = 'light') => {
  if ('vibrate' in navigator) {
    switch (type) {
      case 'light':
        navigator.vibrate(10);
        break;
      case 'medium':
        navigator.vibrate(25);
        break;
      case 'heavy':
        navigator.vibrate(50);
        break;
      case 'selection':
        navigator.vibrate([10, 50, 10]);
        break;
      case 'impact':
        navigator.vibrate([25, 100, 25]);
        break;
    }
  }

  // Web Vibration API fallback for iOS
  if ('webkitVibrate' in navigator) {
    (navigator as any).webkitVibrate(type === 'heavy' ? 50 : type === 'medium' ? 25 : 10);
  }
};

// Touch Optimized Button Component
export const TouchOptimizedButton: React.FC<TouchOptimizedButtonProps> = ({
  children,
  className,
  variant = 'primary',
  size = 'md',
  loading = false,
  hapticFeedback = 'light',
  onLongPress,
  longPressDelay = 500,
  onClick,
  disabled,
  ...props
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isLongPressed, setIsLongPressed] = useState(false);
  const longPressTimer = useRef<NodeJS.Timeout>();
  const touchStartTime = useRef<number>();

  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white border-transparent',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-900 border-transparent',
    ghost: 'bg-transparent hover:bg-gray-100 text-gray-700 border-gray-300',
    destructive: 'bg-red-600 hover:bg-red-700 text-white border-transparent'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm min-h-[40px]',
    md: 'px-4 py-3 text-base min-h-[48px]',
    lg: 'px-6 py-4 text-lg min-h-[56px]',
    xl: 'px-8 py-5 text-xl min-h-[64px]'
  };

  const handleTouchStart = useCallback(() => {
    if (disabled || loading) return;
    
    setIsPressed(true);
    touchStartTime.current = Date.now();
    triggerHapticFeedback(hapticFeedback);

    if (onLongPress) {
      longPressTimer.current = setTimeout(() => {
        setIsLongPressed(true);
        triggerHapticFeedback('medium');
        onLongPress();
      }, longPressDelay);
    }
  }, [disabled, loading, hapticFeedback, onLongPress, longPressDelay]);

  const handleTouchEnd = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    setIsPressed(false);
    
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    // Only trigger click if it wasn't a long press and wasn't too long
    const touchDuration = Date.now() - (touchStartTime.current || 0);
    if (!isLongPressed && touchDuration < 1000 && onClick && !disabled && !loading) {
      onClick(event as React.MouseEvent<HTMLButtonElement>);
    }
    
    setIsLongPressed(false);
  }, [isLongPressed, onClick, disabled, loading]);

  const handleTouchCancel = useCallback(() => {
    setIsPressed(false);
    setIsLongPressed(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  }, []);

  return (
    <motion.button
      className={cn(
        'relative inline-flex items-center justify-center',
        'font-medium rounded-lg border transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        'select-none touch-manipulation',
        'disabled:opacity-50 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        className
      )}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onMouseDown={handleTouchStart}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchCancel}
      disabled={disabled || loading}
      animate={{
        scale: isPressed ? 0.95 : 1,
        opacity: isLongPressed ? 0.8 : 1
      }}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      whileTap={{ scale: 0.95 }}
      {...props}
    >
      {loading && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </motion.div>
      )}
      
      <motion.div
        animate={{ opacity: loading ? 0 : 1 }}
        transition={{ duration: 0.2 }}
        className="flex items-center gap-2"
      >
        {children}
      </motion.div>

      {/* Long press indicator */}
      {isLongPressed && (
        <motion.div
          className="absolute inset-0 bg-white bg-opacity-20 rounded-lg"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0 }}
        />
      )}
    </motion.button>
  );
};

// Swipeable Card Component
export const SwipeableCard: React.FC<SwipeableCardProps> = ({
  children,
  className,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  swipeThreshold = 100,
  leftAction,
  rightAction
}) => {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = () => {
    setIsDragging(true);
    triggerHapticFeedback('selection');
  };

  const handleDrag = (event: any, info: PanInfo) => {
    setDragX(info.offset.x);
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    setIsDragging(false);
    const { offset, velocity } = info;

    // Determine swipe direction and trigger appropriate action
    if (Math.abs(offset.x) > swipeThreshold || Math.abs(velocity.x) > 500) {
      if (offset.x > 0 && onSwipeRight) {
        triggerHapticFeedback('medium');
        onSwipeRight();
      } else if (offset.x < 0 && onSwipeLeft) {
        triggerHapticFeedback('medium');
        onSwipeLeft();
      }
    }

    if (Math.abs(offset.y) > swipeThreshold || Math.abs(velocity.y) > 500) {
      if (offset.y > 0 && onSwipeDown) {
        triggerHapticFeedback('medium');
        onSwipeDown();
      } else if (offset.y < 0 && onSwipeUp) {
        triggerHapticFeedback('medium');
        onSwipeUp();
      }
    }

    // Reset position
    setDragX(0);
  };

  const dragProgress = Math.abs(dragX) / swipeThreshold;
  const actionOpacity = Math.min(dragProgress, 1);

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Left action background */}
      {rightAction && dragX > 0 && (
        <motion.div
          className={cn(
            "absolute inset-y-0 left-0 flex items-center justify-start px-6",
            rightAction.background || "bg-green-500"
          )}
          style={{ width: Math.abs(dragX) }}
          animate={{ opacity: actionOpacity }}
        >
          <div className={cn("flex items-center gap-2", rightAction.color || "text-white")}>
            {rightAction.icon}
            {dragProgress > 0.5 && (
              <span className="font-medium">{rightAction.label}</span>
            )}
          </div>
        </motion.div>
      )}

      {/* Right action background */}
      {leftAction && dragX < 0 && (
        <motion.div
          className={cn(
            "absolute inset-y-0 right-0 flex items-center justify-end px-6",
            leftAction.background || "bg-red-500"
          )}
          style={{ width: Math.abs(dragX) }}
          animate={{ opacity: actionOpacity }}
        >
          <div className={cn("flex items-center gap-2", leftAction.color || "text-white")}>
            {dragProgress > 0.5 && (
              <span className="font-medium">{leftAction.label}</span>
            )}
            {leftAction.icon}
          </div>
        </motion.div>
      )}

      {/* Card content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -200, right: 200 }}
        dragElastic={0.2}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        animate={{
          x: isDragging ? dragX : 0,
          scale: isDragging ? 1.02 : 1
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
        className="relative bg-white touch-manipulation"
      >
        {children}
      </motion.div>
    </div>
  );
};

// Pull to Refresh Component
export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  pullThreshold = 80,
  maxPullDistance = 120,
  refreshing = false
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canPull, setCanPull] = useState(false);

  const handleDragStart = () => {
    setCanPull(window.scrollY === 0);
  };

  const handleDrag = (event: any, info: PanInfo) => {
    if (!canPull || info.offset.y < 0) return;
    
    const distance = Math.min(info.offset.y, maxPullDistance);
    setPullDistance(distance);

    if (distance > pullThreshold) {
      triggerHapticFeedback('selection');
    }
  };

  const handleDragEnd = async (event: any, info: PanInfo) => {
    if (!canPull) return;

    if (pullDistance > pullThreshold && !isRefreshing) {
      setIsRefreshing(true);
      triggerHapticFeedback('medium');
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setPullDistance(0);
    setCanPull(false);
  };

  const pullProgress = pullDistance / pullThreshold;
  const isReady = pullProgress >= 1;

  useEffect(() => {
    setIsRefreshing(refreshing);
  }, [refreshing]);

  return (
    <motion.div
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0.2}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      animate={{
        y: isRefreshing ? pullThreshold : pullDistance
      }}
      className="relative"
    >
      {/* Pull indicator */}
      <motion.div
        className="absolute top-0 left-0 right-0 flex items-center justify-center"
        style={{ height: pullDistance }}
        animate={{
          opacity: pullDistance > 20 ? 1 : 0
        }}
      >
        <div className="flex flex-col items-center">
          <motion.div
            className="w-8 h-8 border-2 border-gray-300 rounded-full flex items-center justify-center mb-2"
            animate={{
              rotate: isRefreshing ? 360 : pullProgress * 360,
              borderColor: isReady ? '#3B82F6' : '#D1D5DB'
            }}
            transition={{
              rotate: isRefreshing ? {
                duration: 1,
                repeat: Infinity,
                ease: "linear"
              } : {
                duration: 0
              }
            }}
          >
            {isRefreshing ? (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <motion.div
                className="w-2 h-2 bg-gray-400 rounded-full"
                animate={{
                  backgroundColor: isReady ? '#3B82F6' : '#9CA3AF'
                }}
              />
            )}
          </motion.div>
          
          <motion.span
            className="text-sm font-medium"
            animate={{
              color: isReady ? '#3B82F6' : '#6B7280'
            }}
          >
            {isRefreshing ? 'Refreshing...' : isReady ? 'Release to refresh' : 'Pull to refresh'}
          </motion.span>
        </div>
      </motion.div>

      {/* Content */}
      <div style={{ paddingTop: isRefreshing ? pullThreshold : pullDistance }}>
        {children}
      </div>
    </motion.div>
  );
};

// Touch feedback hook
export const useTouchFeedback = () => {
  return {
    haptic: triggerHapticFeedback,
    light: () => triggerHapticFeedback('light'),
    medium: () => triggerHapticFeedback('medium'),
    heavy: () => triggerHapticFeedback('heavy'),
    selection: () => triggerHapticFeedback('selection'),
    impact: () => triggerHapticFeedback('impact')
  };
};

export default {
  TouchOptimizedButton,
  SwipeableCard,
  PullToRefresh,
  useTouchFeedback
};